import type { SnapshotPayload } from "@healthos/shared";
import type { IconProps } from "@phosphor-icons/react";
import { CalendarBlank, ClockCounterClockwise, NotePencil, Watch } from "@phosphor-icons/react";
import type { ComponentType } from "react";
import { LinkButton } from "../../primitives/link-button";

type EmptyStateItem = {
  Icon: ComponentType<IconProps>;
  label: string;
  body: string;
};

/*
 * Self-logging leads, because it is the only one of these a member can act on
 * right now. The list used to open with "connect a wearable" and close with
 * "check back after sync", which tells someone without a device to buy hardware
 * or wait, on the one screen that exists because we have nothing from them yet.
 */
const EMPTY_STATE_ITEMS: EmptyStateItem[] = [
  {
    Icon: NotePencil,
    label: "Log it yourself",
    body: "Add to today takes last night's sleep, a meal, water, a vitals reading, or how you feel. A logged night counts toward your sleep read like a wearable's does.",
  },
  {
    Icon: Watch,
    label: "Connect a wearable",
    body: "Phone, watch, ring, and scale data help Snapshot read sleep, movement, stress recovery, and weight trends.",
  },
  {
    Icon: CalendarBlank,
    label: "Add your calendar",
    body: "Calendar signal helps Heartbeat understand busy windows and avoid nudging you at the wrong time.",
  },
  {
    Icon: ClockCounterClockwise,
    label: "Check back after sync",
    body: "Connected sources need a few minutes after the first sync. Snapshot appears once the first read is useful.",
  },
];

/*
 * The card only. The page title is rendered by the route with the same shared
 * header every other surface uses: this component sits in packages/ui and
 * cannot reach it, which is exactly how its own heading drifted from the rest
 * of the product.
 */
export interface SnapshotFreshProps {
  sources: SnapshotPayload["sources"];
  connectionsHref?: string;
}

export function SnapshotFresh({ sources, connectionsHref = "/connectors" }: SnapshotFreshProps) {
  const hasSources = sources.length > 0;

  return (
    <section className="snap-fresh-empty">
      <article
        className="snap-fresh-empty-card snap-stagger-item"
        style={{ animationDelay: "260ms" }}
      >
        <div className="snap-fresh-empty-card-head">
          <p className="snap-fresh-empty-kicker">Listening window</p>
          <h2>
            {hasSources
              ? "Waiting for a clean first read"
              : "Connect sources, then check back later"}
          </h2>
          <div className="snap-fresh-empty-detail">
            {hasSources ? (
              <>
                <p>
                  Your sources are connected, but nothing has reported into a behaviour yet. The
                  first reading that does opens this page. A week of them is what turns single
                  readings into a trend.
                </p>
                <p>
                  Wearables, scales and calendar data each sharpen a different part of the read, and
                  anything you log yourself counts the same. Add what is missing in Connections, or
                  start with one entry.
                </p>
              </>
            ) : (
              <>
                <p>
                  Nothing is feeding Snapshot yet. Wearables and phones send activity, sleep,
                  recovery and weight through Connections, and what you log yourself lands in the
                  same read.
                </p>
                <p>
                  Calendar signal adds timing context, so Heartbeat can understand meetings, travel,
                  and open windows before it recommends anything.
                </p>
              </>
            )}
          </div>

          {hasSources ? (
            <div className="snap-fresh-empty-sources">
              <div className="snap-fresh-sources-label">Picking up signal from</div>
              <div className="snap-fresh-sources-list">
                {sources.map((s, i) => (
                  <span
                    key={s.name}
                    className={`snap-source-pill${s.live ? " live" : ""}`}
                    style={{ animationDelay: `${300 + i * 60}ms` }}
                  >
                    <span className="dot" />
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <nav className="snap-fresh-empty-actions" aria-label="Snapshot setup links">
            <LinkButton asChild>
              <a href={connectionsHref}>Manage connectors</a>
            </LinkButton>
          </nav>
        </div>

        <ul className="snap-fresh-empty-list" aria-label="Snapshot readiness">
          {EMPTY_STATE_ITEMS.map(({ Icon, label, body }) => (
            <li key={label}>
              <span className="snap-fresh-empty-icon" aria-hidden="true">
                <Icon size={18} weight="duotone" />
              </span>
              <span className="snap-fresh-empty-item-copy">
                <b>{label}</b>
                <span>{body}</span>
              </span>
            </li>
          ))}
        </ul>
      </article>
    </section>
  );
}
